import React, { useState, useEffect } from "react";
import { weekendInfoService, imageService } from "../../../services/api";
import { sanitizeHTML } from "../../../utils/htmlSanitizer";

const FIELDS = {
  "СЕО": ["metaTitle","metaDescription"],
  "Основна інформація": ["name","date","description","joinLink"],
  "Верхня картка": ["topCardHeader","topCardDescription","topCardPhotoLink","topCardPhotoMobile"],
  "Нижня картка": ["bottomCardHeader","bottomCardDescription","bottomCardPhotoLink","bottomCardPhotoLinkMobile"],
  "Відео секція": ["videoHeader","videoLink","videoPhotoLink"],
  "Інформаційна секція": ["infoTopText","infoBottomUpperText","infoBottomLowerText"],
};

const ALL_FIELDS = Object.values(FIELDS).flat();

const DISPLAY_NAMES = {
  metaTitle: "Заголовок для СЕО", metaDescription: "Опис для СЕО",
  name: "Назва вікенду", date: "Дата та місце", description: "Опис події",
  joinLink: "Посилання для реєстрації", topCardHeader: "Верхня картка - Заголовок",
  topCardDescription: "Верхня картка - Опис", topCardPhotoLink: "Верхня картка - Фото (Десктоп)",
  topCardPhotoMobile: "Верхня картка - Фото (Мобільне)", bottomCardHeader: "Нижня картка - Заголовок",
  bottomCardDescription: "Нижня картка - Опис", bottomCardPhotoLink: "Нижня картка - Фото (Десктоп)",
  bottomCardPhotoLinkMobile: "Нижня картка - Фото (Мобільне)", videoHeader: "Відео секція - Заголовок",
  videoLink: "Відео секція - Посилання на відео", videoPhotoLink: "Відео секція - Посилання на фото",
  infoTopText: "Інфо секція - Верхній текст", infoBottomUpperText: "Інфо секція - Нижній верхній текст",
  infoBottomLowerText: "Інфо секція - Нижній нижній текст",
};

const TEXTAREA_FIELDS = new Set(["metaDescription","description","topCardHeader","topCardDescription","bottomCardDescription","videoHeader","infoTopText","infoBottomUpperText","infoBottomLowerText"]);
const PHOTO_FIELDS = new Set(["topCardPhotoLink","topCardPhotoMobile","bottomCardPhotoLink","bottomCardPhotoLinkMobile","videoPhotoLink"]);

const emptyForm = () => Object.fromEntries(ALL_FIELDS.map((f) => [f, ""]));

const AdminWeekendInfo = ({ weekend }) => {
  const [formData, setFormData]       = useState(emptyForm());
  const [originalData, setOriginalData] = useState(emptyForm());
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [message, setMessage]         = useState(null);
  const [uploadingImages, setUploadingImages] = useState({});

  useEffect(() => { if (weekend?.id) fetchInfo(); }, [weekend?.id]);

  const flash = (type, text) => {
    setMessage({ type, text });
    setTimeout(() => setMessage(null), 3000);
  };

  const fetchInfo = async () => {
    try {
      setLoading(true);
      const response = await weekendInfoService.getAll(weekend.id);
      const obj = emptyForm();
      response.data.forEach((item) => { obj[item.name] = item.text; });
      setFormData(obj);
      setOriginalData({ ...obj });
    } catch (err) {
      setError("Не вдалося завантажити інформацію вікенду");
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageUpload = async (e, fieldName) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) return flash("error", "Оберіть файл зображення");
    if (file.size > 5 * 1024 * 1024) return flash("error", "Розмір файлу не повинен перевищувати 5MB");

    try {
      setUploadingImages((prev) => ({ ...prev, [fieldName]: true }));
      const fileLink = await imageService.upload(file, "/weekend-info");
      setFormData((prev) => ({ ...prev, [fieldName]: fileLink }));
      flash("success", "Зображення успішно завантажено!");
    } catch {
      flash("error", "Помилка при завантаженні зображення");
    } finally {
      setUploadingImages((prev) => ({ ...prev, [fieldName]: false }));
      e.target.value = "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    window.scrollTo(0, 0);
    try {
      const changed = Object.entries(formData).filter(([name, text]) => originalData[name] !== text);
      if (changed.length === 0) return flash("info", "Немає змін для збереження");

      await Promise.all(
        changed.map(([name, text]) => weekendInfoService.update(weekend.id, name, { text }))
      );
      setOriginalData({ ...formData });
      flash("success", "Інформацію вікенду успішно збережено!");
    } catch (err) {
      flash("error", err.message || "Помилка при збереженні інформації");
    }
  };

  if (loading) return <p style={styles.hint}>Завантаження...</p>;
  if (error)   return <p style={{ color: "#dc3545" }}>{error}</p>;

  const messageColors = {
    success: { bg: "#d4edda", fg: "#155724" },
    error:   { bg: "#f8d7da", fg: "#721c24" },
    info:    { bg: "#cce5ff", fg: "#004085" },
  };

  return (
    <div>
      {message && (
        <div style={{ ...styles.message, backgroundColor: messageColors[message.type]?.bg, color: messageColors[message.type]?.fg }}>
          {message.text}
        </div>
      )}

      <div style={styles.htmlNotice}>
        <strong>Примітка:</strong> Поля підтримують HTML-теги. Використовуйте &lt;br/&gt;, &lt;b&gt;, &lt;span style="..."&gt; тощо.
      </div>

      <form onSubmit={handleSubmit} style={styles.form}>
        {Object.entries(FIELDS).map(([sectionName, fields]) => (
          <div key={sectionName} style={styles.section}>
            <h3 style={styles.sectionTitle}>{sectionName}</h3>
            <div style={styles.sectionGrid}>
              {fields.map((fieldName) => (
                <div key={fieldName} style={TEXTAREA_FIELDS.has(fieldName) ? styles.formGroupFull : styles.formGroup}>
                  <label style={styles.label}>{DISPLAY_NAMES[fieldName]}:</label>

                  {PHOTO_FIELDS.has(fieldName) ? (
                    <div style={styles.photoInputContainer}>
                      <input style={styles.input} name={fieldName} value={formData[fieldName] || ""} onChange={handleInputChange} />
                      <div style={styles.uploadSection}>
                        <span style={styles.orText}>або</span>
                        <label style={{ ...styles.uploadButton, backgroundColor: uploadingImages[fieldName] ? "#6c757d" : "#007bff" }}
                          htmlFor={`img_${fieldName}`}>
                          {uploadingImages[fieldName] ? "Завантаження..." : "📁 Додати фото"}
                        </label>
                        <input type="file" id={`img_${fieldName}`} accept="image/*" style={styles.hiddenInput}
                          disabled={uploadingImages[fieldName]} onChange={(e) => handleImageUpload(e, fieldName)} />
                      </div>
                      {formData[fieldName] && (
                        <img src={formData[fieldName]} alt="preview" style={styles.previewImage} />
                      )}
                    </div>
                  ) : TEXTAREA_FIELDS.has(fieldName) ? (
                    <textarea style={styles.textArea} name={fieldName} value={formData[fieldName] || ""}
                      onChange={handleInputChange} rows={fieldName === "infoTopText" ? 8 : 6} />
                  ) : (
                    <input style={styles.input} name={fieldName} value={formData[fieldName] || ""} onChange={handleInputChange} />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}

        <div style={styles.buttonContainer}>
          <button type="button" style={styles.resetButton} onClick={() => setFormData({ ...originalData })}>Скинути</button>
          <button type="submit" style={styles.button} disabled={Object.values(uploadingImages).some(Boolean)}>
            Зберегти інформацію
          </button>
        </div>
      </form>

      {/* Preview */}
      <div style={styles.previewContainer}>
        <h3 style={styles.previewTitle}>Поточна інформація:</h3>
        {Object.entries(FIELDS).map(([sectionName, fields]) => (
          <div key={sectionName} style={styles.previewSection}>
            <h4 style={styles.previewSectionTitle}>{sectionName}</h4>
            <div style={styles.previewGrid}>
              {fields.map((fieldName) => (
                <div key={fieldName} style={styles.previewItem}>
                  <strong>{DISPLAY_NAMES[fieldName]}:</strong>
                  <div style={styles.previewText}>
                    {PHOTO_FIELDS.has(fieldName) && formData[fieldName] ? (
                      <div>
                        <img src={formData[fieldName]} alt="" style={styles.thumbnailImage} /><br />
                        <a href={formData[fieldName]} target="_blank" rel="noopener noreferrer">{formData[fieldName]}</a>
                      </div>
                    ) : formData[fieldName] ? (
                      <div dangerouslySetInnerHTML={{ __html: sanitizeHTML(formData[fieldName]) }} style={styles.htmlPreview} />
                    ) : (
                      <span style={{ fontStyle: "italic", color: "#999" }}>Не встановлено</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  hint: { padding: "2rem", textAlign: "center", color: "#666" },
  message: { padding: "1rem", marginBottom: "1rem", borderRadius: "4px" },
  htmlNotice: { padding: "1rem", backgroundColor: "#e3f2fd", border: "1px solid #2196f3", borderRadius: "4px", marginBottom: "1.5rem", fontSize: "0.9rem", color: "#1976d2" },
  form: { display: "flex", flexDirection: "column", gap: "2rem" },
  section: { padding: "1.5rem", backgroundColor: "white", borderRadius: "8px", border: "1px solid #e9ecef" },
  sectionTitle: { marginBottom: "1rem", fontSize: "1.1rem", color: "var(--secondary-color)", fontWeight: 600 },
  sectionGrid: { display: "grid", gap: "1rem", gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))" },
  formGroup: { display: "flex", flexDirection: "column" },
  formGroupFull: { display: "flex", flexDirection: "column", gridColumn: "1 / -1" },
  label: { marginBottom: "0.5rem", fontWeight: 600 },
  input: { padding: "0.8rem", border: "1px solid #ddd", borderRadius: "4px", fontFamily: "'UAFSans', sans-serif", outline: "none" },
  textArea: { padding: "0.8rem", border: "1px solid #ddd", borderRadius: "4px", fontFamily: "'UAFSans', sans-serif",resize: "vertical", outline: "none" },
  photoInputContainer: { display: "flex", flexDirection: "column", gap: "0.5rem" },
  uploadSection: { display: "flex", alignItems: "center", gap: "0.5rem" },
  orText: { fontSize: "0.9rem", color: "#666", fontStyle: "italic" },
  uploadButton: { display: "inline-block", padding: "0.6rem 1rem", color: "white", borderRadius: "4px", cursor: "pointer", fontSize: "0.9rem", fontFamily: "'UAFSans', sans-serif", userSelect: "none" },
  hiddenInput: { display: "none" },
  previewImage: { maxWidth: "200px", maxHeight: "150px", objectFit: "contain", border: "1px solid #ddd", borderRadius: "4px" },
  thumbnailImage: { width: "60px", height: "60px", objectFit: "cover", borderRadius: "4px", border: "1px solid #ddd" },
  button: { padding: "0.8rem 1.5rem", backgroundColor: "green", color: "white", border: "none", borderRadius: "4px", fontFamily: "'UAFSans', sans-serif", fontWeight: 500, cursor: "pointer" },
  resetButton: { padding: "0.8rem 1.5rem", backgroundColor: "#6c757d", color: "white", border: "none", borderRadius: "4px", fontFamily: "'UAFSans', sans-serif", fontWeight: 500, cursor: "pointer", marginRight: "1rem" },
  buttonContainer: { display: "flex", justifyContent: "flex-end" },
  previewContainer: { marginTop: "2rem", padding: "1.5rem", backgroundColor: "#f8f9fa", borderRadius: "8px", border: "1px solid #e9ecef" },
  previewTitle: { marginBottom: "1.5rem", fontSize: "1.2rem", color: "var(--secondary-color)" },
  previewSection: { marginBottom: "1.5rem", padding: "1rem", backgroundColor: "white", borderRadius: "4px", border: "1px solid #e9ecef" },
  previewSectionTitle: { marginBottom: "1rem", fontSize: "1rem", color: "var(--secondary-color)", fontWeight: 600 },
  previewGrid: { display: "grid", gap: "1rem", gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))" },
  previewItem: { padding: "0.75rem", backgroundColor: "#f8f9fa", borderRadius: "4px", border: "1px solid #e9ecef" },
  previewText: { margin: "0.5rem 0 0 0", color: "#6c757d" },
  htmlPreview: { border: "1px solid #e0e0e0", padding: "0.5rem", backgroundColor: "#fafafa", borderRadius: "3px", fontSize: "0.9rem", lineHeight: "1.4" },
};

export default AdminWeekendInfo;